import mapboxgl from "mapbox-gl";
import { SWITZERLAND_CENTER } from "@utils/maps/mapStyles";
import env from "@configs/env";
import configureMapboxWorker from "@utils/maps/configureMapboxWorker";

const BUILDINGS_LAYER_ID = "cms-3d-buildings-layer";
const SKY_LAYER_ID = "cms-sky-layer";
const TERRAIN_SOURCE_ID = "cms-mapbox-dem-source";

function maskToken(token = "") {
  const value = String(token || "");
  if (value.length <= 10) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

export function createMapboxInstance({
  container,
  token,
  style,
  zoom = 7,
  center = SWITZERLAND_CENTER,
}) {
  configureMapboxWorker();
  if (process.env.REACT_APP_DEBUG_ENV) {
  }
  mapboxgl.accessToken = token;
  const map = new mapboxgl.Map({
    container,
    style,
    center,
    zoom,
  });
  if (process.env.REACT_APP_DEBUG_ENV) {
  }
  map.addControl(new mapboxgl.NavigationControl(), "top-right");
  return map;
}

export function destroyMapboxInstance(map) {
  if (!map) return;
  map.remove();
}

export function setMapStyle(map, style) {
  if (!map || !style) return;
  map.setStyle(style);
}

export function applyMap3D(map, enabled) {
  if (!map || !map.isStyleLoaded()) return;

  if (map.getLayer(BUILDINGS_LAYER_ID)) map.removeLayer(BUILDINGS_LAYER_ID);
  if (map.getLayer(SKY_LAYER_ID)) map.removeLayer(SKY_LAYER_ID);
  const activeTerrain = map.getTerrain?.();
  if (activeTerrain?.source === TERRAIN_SOURCE_ID) {
    map.setTerrain(null);
  }
  if (map.getSource(TERRAIN_SOURCE_ID)) {
    map.removeSource(TERRAIN_SOURCE_ID);
  }

  if (!enabled) {
    map.easeTo({ pitch: 0, bearing: 0, duration: 800 });
    return;
  }

  map.addSource(TERRAIN_SOURCE_ID, {
    type: "raster-dem",
    url: "mapbox://mapbox.mapbox-terrain-dem-v1",
    tileSize: 512,
    maxzoom: 14,
  });
  map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration: 1.2 });
  if (!map.getLayer(SKY_LAYER_ID)) {
    map.addLayer({
      id: SKY_LAYER_ID,
      type: "sky",
      paint: {
        "sky-type": "atmosphere",
        "sky-atmosphere-sun-intensity": 10,
      },
    });
  }

  if (map.getSource("composite")) {
    const labelLayerId = (map.getStyle()?.layers || []).find(
      (layer) => layer.type === "symbol",
    )?.id;
    map.addLayer(
      {
        id: BUILDINGS_LAYER_ID,
        source: "composite",
        "source-layer": "building",
        filter: ["==", "extrude", "true"],
        type: "fill-extrusion",
        minzoom: 15,
        paint: {
          "fill-extrusion-color": "#9ec5ff",
          "fill-extrusion-height": ["get", "height"],
          "fill-extrusion-base": ["get", "min_height"],
          "fill-extrusion-opacity": 0.65,
        },
      },
      labelLayerId,
    );
  }

  map.easeTo({ pitch: 60, bearing: -20, duration: 800 });
}

export function sync3DWhenStyleReady(map, enabled) {
  if (!map) return () => {};
  if (map.isStyleLoaded()) {
    applyMap3D(map, enabled);
    return () => {};
  }
  const handleStyleLoad = () => applyMap3D(map, enabled);
  map.once("style.load", handleStyleLoad);
  return () => {
    map.off("style.load", handleStyleLoad);
  };
}
